/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card, Space, Typography, Button, Tooltip, message, Modal, Table, Input, Tag, Select, Checkbox, Tabs } from 'antd';
import { CloudOutlined, SaveOutlined, AppstoreOutlined, UploadOutlined, CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import WeatherCalendarEditor from './WeatherCalendarEditor';
import type { DailyWeatherPoint } from '../../utils/climatePresets';
import type { WeatherDataPoint, WeatherImportProvider, WeatherMapItem } from '../../types/simulation';
import {
  useCreateWeatherSeriesMutation,
  useAddWeatherDataPointsMutation,
  useListWeatherMapsQuery,
  useActivateWeatherMapMutation,
  useSaveWeatherSnapshotMutation,
  useGetWeatherDataPointsQuery,
  useGetWeatherSeriesQuery,
  useGetSaveRuntimeSessionQuery,
  useApplyRuntimeReplayMutation,
  useGetWeatherImportLocationsQuery,
  useImportWeatherSeriesMutation,
  useCloneWeatherSeriesMutation,
  useGetWeatherYearPolicyQuery,
  useUpdateWeatherYearPolicyMutation,
} from '../../api/simulationApi';
import {
  dayOfYearFromIsoDate,
  isoDateFromDayOfYear,
  buildWeatherPointExtra,
  estimateAstronomicalDaylightHours,
  utcYearFromIsoDate,
} from './weatherMapUtils';

const { Text } = Typography;
const { Search } = Input;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const SESSION_APPLY_SOURCE_PREFIX = 'session_apply:';
const WEATHER_POINT_CHUNK_SIZE = 45;
const FARM_LOCATION_KEY = '__farm__';
const WEATHER_PROVIDER_OPTIONS: Array<{ value: WeatherImportProvider; label: string }> = [
  { value: 'nasa_power', label: 'NASA POWER' },
  { value: 'open_meteo', label: 'Open-Meteo' },
];

const styles = {
  summary: css({
    fontSize: 12,
    color: '#888',
    marginTop: 8,
  }),
  toolbar: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 12,
  }),
};

// ─── Map list (server-side: system presets + user maps) ─────

const doyToDate = (doy: number): string => {
  let remaining = doy;
  for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
    if (remaining <= DAYS_IN_MONTH[monthIndex]) {
      return `${String(monthIndex + 1).padStart(2, '0')}-${String(remaining).padStart(2, '0')}`;
    }
    remaining -= DAYS_IN_MONTH[monthIndex];
  }
  return '12-31';
};

const parsePointExtra = (extra: WeatherDataPoint['extra'] | string | null | undefined): Record<string, unknown> => {
  if (!extra) return {};
  if (typeof extra === 'string') {
    try {
      return JSON.parse(extra) ?? {};
    } catch {
      return {};
    }
  }
  return extra;
};

const formatLatitude = (value: unknown): string => {
  const lat = Number(value);
  if (!Number.isFinite(lat)) return '';
  const hemisphere = lat < 0 ? 'S' : 'N';
  return `${Math.abs(lat).toFixed(2)}°${hemisphere}`;
};

const buildWeatherSeriesPayload = (points: DailyWeatherPoint[], year: number) => points.map((point) => ({
  observedAt: isoDateFromDayOfYear(point.dayOfYear, year),
  tempMin: point.tempMin,
  tempMax: point.tempMax,
  tempAvg: +((point.tempMin + point.tempMax) / 2).toFixed(1),
  precipitationMm: point.precipitationMm,
  humidityPct: point.humidityPct,
  windSpeedMs: point.windSpeedMs ?? null,
  gdd: null,
  extra: buildWeatherPointExtra(point),
}));

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const round1 = (value: number): number => Math.round(value * 10) / 10;

const rounded = (value: unknown, precision = 2): number | null => {
  const num = Number(value);
  return Number.isFinite(num) ? Number(num.toFixed(precision)) : null;
};

const dailyPointEquals = (a: DailyWeatherPoint, b: DailyWeatherPoint): boolean => (
  (a.weatherCode ?? null) === (b.weatherCode ?? null)
  && (a.precipitationHours ?? null) === (b.precipitationHours ?? null)
  && (a.thunderstormCode ?? null) === (b.thunderstormCode ?? null)
  && (a.windSpeedMs ?? null) === (b.windSpeedMs ?? null)
  && rounded(a.tempMin, 1) === rounded(b.tempMin, 1)
  && rounded(a.tempMax, 1) === rounded(b.tempMax, 1)
  && rounded(a.precipitationMm, 2) === rounded(b.precipitationMm, 2)
  && rounded(a.humidityPct, 0) === rounded(b.humidityPct, 0)
  && rounded(a.cloudCover, 3) === rounded(b.cloudCover, 3)
  && rounded(a.outdoorSunHours, 2) === rounded(b.outdoorSunHours, 2)
  && rounded(a.solarPpfdUmol, 0) === rounded(b.solarPpfdUmol, 0)
);

const buildChangedWeatherSeriesPayload = (
  current: DailyWeatherPoint[],
  source: DailyWeatherPoint[] | null,
  year: number,
) => {
  if (!source || source.length === 0) return buildWeatherSeriesPayload(current, year);
  const sourceByDoy = new Map(source.map((point) => [point.dayOfYear, point]));
  return buildWeatherSeriesPayload(
    current.filter((point) => {
      const sourcePoint = sourceByDoy.get(point.dayOfYear);
      return !sourcePoint || !dailyPointEquals(point, sourcePoint);
    }),
    year,
  );
};

const cloneDailyPoints = (points: DailyWeatherPoint[]) => points.map((point) => ({ ...point }));

const weatherDataPointsToDaily = (points: WeatherDataPoint[]): DailyWeatherPoint[] => (
  points.map((point, index) => {
    const extra = parsePointExtra(point.extra);
    const dayOfYear = dayOfYearFromIsoDate(point.observedAt) ?? (index + 1);
    // The server resolves the light fields per point (day length from the series
    // latitude, cloud/sun from radiation); the local estimate is a last resort only.
    const astronomicalDaylightHours = Number(
      point.astronomicalDaylightHours ?? estimateAstronomicalDaylightHours(dayOfYear),
    );
    const cloudCover = Number(point.cloudCover ?? extra.cloudCover ?? 0.5);
    const outdoorSunHours = Number(
      point.outdoorSunHours ?? extra.outdoorSunHours ?? extra.sunHours
        ?? round1(astronomicalDaylightHours * (1 - cloudCover)),
    );
    return {
      dayOfYear,
      weatherCode: typeof extra.weatherCode === 'number' ? extra.weatherCode : null,
      precipitationHours: typeof extra.precipitationHours === 'number' ? extra.precipitationHours : null,
      thunderstormCode: typeof extra.thunderstormCode === 'number' ? extra.thunderstormCode : null,
      windSpeedMs: point.windSpeedMs,
      extra,
      date: point.observedAt?.slice(5, 10) ?? doyToDate(dayOfYear),
      tempMin: point.tempMin ?? 0,
      tempMax: point.tempMax ?? 0,
      precipitationMm: point.precipitationMm ?? 0,
      humidityPct: point.humidityPct ?? 60,
      astronomicalDaylightHours,
      cloudCover,
      outdoorSunHours,
      solarPpfdUmol: Number(point.solarPpfdUmol ?? extra.solarPpfdUmol ?? 650),
      source: (extra.sourceType as DailyWeatherPoint['source']) ?? 'climate_norm',
      dataKind: (extra.dataKind as DailyWeatherPoint['dataKind']) ?? undefined,
    };
  })
);

// ─── Component ─────────────────────────────────────────────────────

export interface WeatherMapPanelProps {
  saveId: string;
  farmId: string;
  loading?: boolean;
}

const WeatherMapPanel: React.FC<WeatherMapPanelProps> = ({ saveId, farmId }) => {
  const { t } = useTranslation('simulation');
  const [dailyPoints, setDailyPoints] = useState<DailyWeatherPoint[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveMapName, setSaveMapName] = useState('');
  const [importMapName, setImportMapName] = useState('');
  const [importProvider, setImportProvider] = useState<WeatherImportProvider>('nasa_power');
  const [importYear, setImportYear] = useState<number | null>(null);
  const [importLocationKey, setImportLocationKey] = useState(FARM_LOCATION_KEY);
  const [mapImportToSessionYear, setMapImportToSessionYear] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [activeWeatherSeriesId, setActiveWeatherSeriesId] = useState<string | null>(null);
  const [activeWeatherSeriesYear, setActiveWeatherSeriesYear] = useState<number | null>(null);
  const [loadedSessionWeatherSeriesId, setLoadedSessionWeatherSeriesId] = useState<string | null>(null);
  const [sourceDailyPoints, setSourceDailyPoints] = useState<DailyWeatherPoint[] | null>(null);
  /** When a map is activated, hold its seriesId so we can load data points */
  const [loadingSeriesId, setLoadingSeriesId] = useState<string | null>(null);

  const [createWeatherSeries, { isLoading: creatingSeries }] = useCreateWeatherSeriesMutation();
  const [addDataPoints, { isLoading: addingPoints }] = useAddWeatherDataPointsMutation();
  const [activateWeatherMap, { isLoading: activating }] = useActivateWeatherMapMutation();
  const [saveWeatherSnapshot] = useSaveWeatherSnapshotMutation();
  const [applyRuntimeReplay, { isLoading: applyingRuntimeReplay }] = useApplyRuntimeReplayMutation();
  const [importWeatherSeries, { isLoading: importingWeather }] = useImportWeatherSeriesMutation();
  const [cloneWeatherSeries, { isLoading: cloningWeather }] = useCloneWeatherSeriesMutation();
  const [updateWeatherYearPolicy, { isLoading: updatingYearPolicy }] = useUpdateWeatherYearPolicyMutation();
  const { data: activeSession } = useGetSaveRuntimeSessionQuery(saveId);
  const activeSessionYear = useMemo(
    () => activeSession?.simDate ? utcYearFromIsoDate(activeSession.simDate) : new Date().getUTCFullYear(),
    [activeSession?.simDate],
  );
  const { data: weatherYearPolicy } = useGetWeatherYearPolicyQuery(activeSession?.weatherSeriesId || '', {
    skip: !activeSession?.weatherSeriesId,
  });
  const { data: activeSessionWeatherSeries } = useGetWeatherSeriesQuery(activeSession?.weatherSeriesId || '', {
    skip: !activeSession?.weatherSeriesId,
  });
  const { data: activeSessionWeatherPoints } = useGetWeatherDataPointsQuery(
    {
      seriesId: activeSession?.weatherSeriesId || '',
      from: `${activeSessionYear}-01-01`,
      to: `${activeSessionYear}-12-31`,
      limit: 370,
    },
    { skip: !activeSession?.weatherSeriesId },
  );
  const { data: importLocationData } = useGetWeatherImportLocationsQuery(undefined, {
    skip: !mapModalOpen,
  });
  // Server-side map list (system presets + user maps)
  const { data: allMapsList } = useListWeatherMapsQuery(farmId);

  // Load data points for a selected / activated map
  const { data: userMapPoints } = useGetWeatherDataPointsQuery(
    { seriesId: loadingSeriesId! },
    { skip: !loadingSeriesId },
  );

  useEffect(() => {
    const sessionWeatherSeriesId = activeSession?.weatherSeriesId || null;
    if (!sessionWeatherSeriesId || !activeSessionWeatherPoints || activeSessionWeatherPoints.length === 0) return;
    if (loadedSessionWeatherSeriesId === sessionWeatherSeriesId) return;
    const showingPreviousSessionWeather = activeWeatherSeriesId === loadedSessionWeatherSeriesId;
    if (initialized && activeWeatherSeriesId && activeWeatherSeriesId !== sessionWeatherSeriesId && !showingPreviousSessionWeather) return;

    const daily = weatherDataPointsToDaily(activeSessionWeatherPoints);
    // A real "current year" map is intentionally partial (observed-to-date +
    // forecast tail), so don't require a full ~365-day series here — any non-empty
    // load is a valid map to display.
    if (daily.length > 0) {
      setDailyPoints(daily);
      setActiveWeatherSeriesId(sessionWeatherSeriesId);
      setActiveWeatherSeriesYear(utcYearFromIsoDate(activeSessionWeatherPoints[0]?.observedAt));
      setLoadedSessionWeatherSeriesId(sessionWeatherSeriesId);
      setSourceDailyPoints(cloneDailyPoints(daily));
      setInitialized(true);
    }
  }, [
    activeSession?.weatherSeriesId,
    activeSessionWeatherPoints,
    activeWeatherSeriesId,
    initialized,
    loadedSessionWeatherSeriesId,
  ]);

  // After loading map data points, convert and set
  useEffect(() => {
    if (loadingSeriesId && userMapPoints && userMapPoints.length > 0) {
      const sourceSeriesId = loadingSeriesId;
      const daily = weatherDataPointsToDaily(userMapPoints);
      setDailyPoints(daily);
      setActiveWeatherSeriesId(sourceSeriesId);
      setActiveWeatherSeriesYear(utcYearFromIsoDate(userMapPoints[0]?.observedAt));
      if (sourceSeriesId === activeSession?.weatherSeriesId) {
        setLoadedSessionWeatherSeriesId(sourceSeriesId);
      }
      setSourceDailyPoints(cloneDailyPoints(daily));
      setInitialized(true);
      setLoadingSeriesId(null);
      setMapModalOpen(false);
      message.success(i18n.t('simulation:weatherMap.loaded'));
    }
  }, [loadingSeriesId, userMapPoints, activeSession?.weatherSeriesId]);

  const addDataPointsInChunks = useCallback(async (
    seriesId: string,
    points: ReturnType<typeof buildWeatherSeriesPayload>,
  ) => {
    for (const chunk of chunkArray(points, WEATHER_POINT_CHUNK_SIZE)) {
      await addDataPoints({ seriesId, points: chunk }).unwrap();
    }
  }, [addDataPoints]);

  // ─── Map modal data ────────────────────────────────

  const allMaps = useMemo(() => allMapsList ?? [], [allMapsList]);
  const appliedWeatherMapId = useMemo(() => {
    const metadata = activeSessionWeatherSeries?.metadata as Record<string, unknown> | null;
    const sourceSeriesId = typeof metadata?.sourceSeriesId === 'string' ? metadata.sourceSeriesId : null;
    return sourceSeriesId && allMaps.some((map) => map.id === sourceSeriesId)
      ? sourceSeriesId
      : activeSession?.weatherSeriesId || null;
  }, [activeSession?.weatherSeriesId, activeSessionWeatherSeries?.metadata, allMaps]);

  const importYearOptions = useMemo(() => {
    const fallbackYear = new Date().getUTCFullYear() - 1;
    const years = importLocationData?.recentCompleteYears?.length
      ? importLocationData.recentCompleteYears
      : [fallbackYear, fallbackYear - 1, fallbackYear - 2, fallbackYear - 3, fallbackYear - 4];
    return years.map((year) => ({ value: year, label: String(year) }));
  }, [importLocationData]);

  const importLocationOptions = useMemo(() => [
    { value: FARM_LOCATION_KEY, label: t('weatherMap.farmLocation') },
    ...(importLocationData?.locations || []).map((location) => ({
      value: location.key,
      label: location.region || location.state ? `${location.name}, ${location.region || location.state}` : location.name,
    })),
  ], [importLocationData, t]);

  useEffect(() => {
    if (mapModalOpen && importYear == null && importYearOptions.length > 0) {
      setImportYear(importYearOptions[0].value);
    }
  }, [mapModalOpen, importYear, importYearOptions]);

  const userMapCount = useMemo(
    () => allMaps.filter(m => !m.isSystem).length,
    [allMaps],
  );

  const filteredMaps = useMemo(() => {
    if (!filterText) return allMaps;
    const lower = filterText.toLowerCase();
    return allMaps.filter(m =>
      m.name.toLowerCase().includes(lower)
      || (m.metadata as Record<string, unknown>)?.regionName?.toString().toLowerCase().includes(lower)
      || (m.metadata as Record<string, unknown>)?.yearTypeName?.toString().toLowerCase().includes(lower),
    );
  }, [filterText, allMaps]);

  // Extract unique regions for filters
  const regionFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const m of allMaps) {
      const meta = m.metadata as Record<string, unknown> | null;
      const regionId = meta?.region as string | undefined;
      const regionName = (meta?.regionName as string | undefined) || m.name;
      if (regionId && regionName && !seen.has(regionId)) {
        seen.set(regionId, regionName);
      }
    }
    return Array.from(seen.entries()).map(([value, text]) => ({ text, value }));
  }, [allMaps]);

  const mapColumns = useMemo(() => [
    {
      title: t('weatherMap.columns.regionName'),
      dataIndex: 'name',
      key: 'name',
      filters: [
        { text: t('weatherMap.myMaps'), value: '__user__' },
        ...regionFilters,
      ],
      onFilter: (value: unknown, record: WeatherMapItem) => {
        if (value === '__user__') return !record.isSystem;
        return (record.metadata as Record<string, unknown>)?.region === value;
      },
      render: (_: unknown, record: WeatherMapItem) => {
        const meta = record.metadata as Record<string, unknown> | null;
        const isAppliedToSession = record.id === appliedWeatherMapId;
        const latitudeLabel = formatLatitude(meta?.lat);
        const yearTypeLabel = typeof meta?.yearTypeName === 'string' ? meta.yearTypeName : null;
        const regionLabel = meta?.regionName
          ? `${meta.regionName} (${meta.koppen ?? ''}${latitudeLabel ? `, ${latitudeLabel}` : ''})`
          : undefined;
        return (
          <Space direction="vertical" size={0}>
            <Space>
              <span>{record.name}</span>
              {record.isSystem && <Tag color="default">{t('weatherMap.tags.system')}</Tag>}
              {!record.isSystem && <Tag color="blue">{t('weatherMap.tags.mine')}</Tag>}
              {record.isSystem && yearTypeLabel && <Tag color="geekblue">{yearTypeLabel}</Tag>}
              {isAppliedToSession && <Tag color="green" icon={<CheckCircleOutlined />}>{t('weatherMap.tags.inUse')}</Tag>}
            </Space>
            {regionLabel && <Text type="secondary" style={{ fontSize: 11 }}>{regionLabel}</Text>}
          </Space>
        );
      },
    },
    {
      title: t('weatherMap.columns.avgTemp'),
      key: 'avgTemp',
      width: 80,
      render: (_: unknown, record: WeatherMapItem) => {
        const v = (record.metadata as Record<string, unknown>)?.avgTemp;
        return v != null ? `${v}°C` : '—';
      },
    },
    {
      title: t('weatherMap.columns.precipitation'),
      key: 'totalPrecip',
      width: 90,
      render: (_: unknown, record: WeatherMapItem) => {
        const v = (record.metadata as Record<string, unknown>)?.totalPrecip;
        return v != null ? t('weatherMap.columns.mmPerYear', { value: v }) : '—';
      },
    },
  ], [appliedWeatherMapId, regionFilters, t]);

  // ─── Load map into calendar (activate via server) ────

  const handleSelectMap = useCallback(async (record: WeatherMapItem) => {
    if (record.isActive) {
      // The backend flag means the map already has live data and needs no snapshot unpack.
      setLoadingSeriesId(record.id);
      setMapModalOpen(false);
      return;
    }
    try {
      const activated = await activateWeatherMap({ seriesId: record.id, farmId }).unwrap();
      setLoadingSeriesId(activated.id);
    } catch {
      message.error(i18n.t('simulation:weatherMap.activateFailed'));
    }
  }, [farmId, activateWeatherMap]);

  // ─── Save current map ─────────────────────────────

  const handleSaveMap = useCallback(async () => {
    if (!saveMapName.trim() || dailyPoints.length === 0) return;
    try {
      const series = await createWeatherSeries({
        farmId,
        name: saveMapName.trim(),
        seriesType: 'weather_map',
        source: 'user',
        resolution: 'daily',
      }).unwrap();

      const year = new Date().getUTCFullYear();
      await addDataPointsInChunks(series.id, buildWeatherSeriesPayload(dailyPoints, year));

      // Pack into cold snapshot
      await saveWeatherSnapshot(series.id).unwrap();

      message.success(i18n.t('simulation:weatherMap.saved', { name: saveMapName }));
      setSaveModalOpen(false);
      setSaveMapName('');
    } catch {
      message.error(i18n.t('simulation:weatherMap.saveFailed'));
    }
  }, [farmId, dailyPoints, saveMapName, createWeatherSeries, addDataPointsInChunks, saveWeatherSnapshot]);

  const handleImportHistoricalWeather = useCallback(async () => {
    const selectedYear = importYear ?? importYearOptions[0]?.value;
    if (!selectedYear) return;

    try {
      const targetYear = mapImportToSessionYear && activeSessionYear !== selectedYear ? activeSessionYear : undefined;
      const imported = await importWeatherSeries({
        farmId,
        name: importMapName.trim() || undefined,
        provider: importProvider,
        fallbackProvider: importProvider === 'nasa_power' ? 'open_meteo' : 'nasa_power',
        year: selectedYear,
        targetYear,
        locationKey: importLocationKey === FARM_LOCATION_KEY ? undefined : importLocationKey,
      }).unwrap();

      setLoadingSeriesId(imported.series.id);
      setMapModalOpen(false);
      setImportMapName('');
      message.success(i18n.t('simulation:weatherMap.imported', { count: imported.pointCount, name: imported.series.name }));
    } catch {
      message.error(i18n.t('simulation:weatherMap.importFailed'));
    }
  }, [
    activeSessionYear,
    farmId,
    importLocationKey,
    importMapName,
    importProvider,
    importWeatherSeries,
    importYear,
    importYearOptions,
    mapImportToSessionYear,
  ]);

  // ─── Apply to simulation ───────────────────────────────

  const announceReplayResult = useCallback((applyMode?: string, fromDate?: string | null) => {
    if (applyMode === 'future_only') {
      message.success(i18n.t('simulation:weatherMap.apply.futureOnly'));
    } else if (applyMode === 'rebind_only') {
      message.success(i18n.t('simulation:weatherMap.apply.rebindOnly'));
    } else if (applyMode === 'replay') {
      message.success(fromDate
        ? i18n.t('simulation:weatherMap.apply.replayFrom', { date: fromDate })
        : i18n.t('simulation:weatherMap.apply.replay'));
    } else {
      message.success(i18n.t('simulation:weatherMap.apply.upToDate'));
    }
  }, []);

  const handleApplyToSimulation = useCallback(async () => {
    if (dailyPoints.length === 0) return;
    try {
      if (activeSession?.id) {
        const sessionYear = utcYearFromIsoDate(activeSession.simDate);
        const sessionApplySource = `${SESSION_APPLY_SOURCE_PREFIX}${activeSession.id}`;
        const changedPoints = buildChangedWeatherSeriesPayload(dailyPoints, sourceDailyPoints, sessionYear);
        const sourceYearMismatch = activeWeatherSeriesYear != null && activeWeatherSeriesYear !== sessionYear;

        // Locked-environment model: the save's climate map can't be swapped, but the
        // player may hand-edit its days. When the panel shows the session's OWN series,
        // write the edited days back IN PLACE and force a weather rebuild — no series
        // swap, so the WEATHER_MAP_LOCKED guard isn't tripped and the retro edit replays
        // from the earliest snapshot. (Loading a DIFFERENT map still goes through clone
        // below and will surface the lock, which is the intended behaviour.)
        const sessionSeriesId = activeSession.weatherSeriesId || null;
        const editingSessionSeries = Boolean(
          sessionSeriesId && activeWeatherSeriesId === sessionSeriesId && !sourceYearMismatch,
        );
        if (editingSessionSeries && sessionSeriesId) {
          if (changedPoints.length === 0) {
            message.info(i18n.t('simulation:weatherMap.apply.noChanges'));
            return;
          }
          await addDataPointsInChunks(sessionSeriesId, changedPoints);
          const replayResult = await applyRuntimeReplay({
            saveId,
            config: activeSession.config || {},
            forceWeatherRebuild: true,
            awaitReplay: true,
            expectedInputRevision: activeSession.inputRevision,
          }).unwrap();
          setSourceDailyPoints(cloneDailyPoints(dailyPoints));
          announceReplayResult(replayResult.applyMode, replayResult.invalidation?.fromDate);
          return;
        }

        let sessionWeatherSeriesId: string | null = null;

        if (activeWeatherSeriesId && sourceDailyPoints && (changedPoints.length > 0 || sourceYearMismatch)) {
          const cloned = await cloneWeatherSeries({
            sourceSeriesId: activeWeatherSeriesId,
            farmId,
            name: `Session weather ${activeSession.id.slice(0, 8)}`,
            seriesType: 'synthetic',
            source: sessionApplySource,
            targetYear: sessionYear,
            metadata: {
              sessionId: activeSession.id,
              generatedBy: 'weather-map-panel',
              editedDays: changedPoints.length,
            },
          }).unwrap();
          sessionWeatherSeriesId = cloned.id;
          if (changedPoints.length > 0) {
            await addDataPointsInChunks(sessionWeatherSeriesId, changedPoints);
          }
        } else if (activeWeatherSeriesId && sourceDailyPoints) {
          sessionWeatherSeriesId = activeWeatherSeriesId;
        }

        if (!sessionWeatherSeriesId) {
          if (activeSession.weatherSeriesId) {
            const cloned = await cloneWeatherSeries({
              sourceSeriesId: activeSession.weatherSeriesId,
              farmId,
              name: `Session weather ${activeSession.id.slice(0, 8)}`,
              seriesType: 'synthetic',
              source: sessionApplySource,
              targetYear: sessionYear,
              metadata: {
                sessionId: activeSession.id,
                generatedBy: 'weather-map-panel',
              },
            }).unwrap();
            sessionWeatherSeriesId = cloned.id;
          } else {
            const sessionSeries = await createWeatherSeries({
              farmId,
              name: `Session weather ${activeSession.id.slice(0, 8)}`,
              seriesType: 'synthetic',
              source: sessionApplySource,
              resolution: 'daily',
              metadata: {
                sessionId: activeSession.id,
                generatedBy: 'weather-map-panel',
              },
            }).unwrap();
            sessionWeatherSeriesId = sessionSeries.id;
          }
          await addDataPointsInChunks(sessionWeatherSeriesId, buildWeatherSeriesPayload(dailyPoints, sessionYear));
        }

        const replayResult = await applyRuntimeReplay({
          saveId,
          config: activeSession.config || {},
          weatherSeriesId: sessionWeatherSeriesId,
          awaitReplay: true,
          expectedInputRevision: activeSession.inputRevision,
        }).unwrap();

        setActiveWeatherSeriesId(sessionWeatherSeriesId);
        setActiveWeatherSeriesYear(sessionYear);
        setLoadedSessionWeatherSeriesId(sessionWeatherSeriesId);
        setSourceDailyPoints(cloneDailyPoints(dailyPoints));

        announceReplayResult(replayResult.applyMode, replayResult.invalidation?.fromDate);
        return;
      }

      message.error(i18n.t('simulation:weatherMap.apply.noSession'));
    } catch {
      message.error(i18n.t('simulation:weatherMap.apply.failed'));
    }
  }, [
    activeSession,
    activeWeatherSeriesId,
    activeWeatherSeriesYear,
    addDataPointsInChunks,
    announceReplayResult,
    cloneWeatherSeries,
    createWeatherSeries,
    dailyPoints,
    farmId,
    saveId,
    sourceDailyPoints,
    applyRuntimeReplay,
  ]);

  // Summary
  const summary = useMemo(() => {
    if (dailyPoints.length === 0) return null;
    const temps = dailyPoints.map(p => (p.tempMin + p.tempMax) / 2);
    const avgTemp = temps.reduce((a, b) => a + b, 0) / temps.length;
    const totalPrecip = dailyPoints.reduce((a, p) => a + p.precipitationMm, 0);
    const avgHumidity = dailyPoints.reduce((a, p) => a + p.humidityPct, 0) / dailyPoints.length;
    // A partial real-year map (observed-to-date + forecast) doesn't cover a full
    // year, so don't label its precip total "per year".
    const partial = dailyPoints.some(p => p.dataKind) && dailyPoints.length < 360;
    return {
      avgTemp: avgTemp.toFixed(1),
      totalPrecip: totalPrecip.toFixed(0),
      avgHumidity: avgHumidity.toFixed(0),
      partial,
      dayCount: dailyPoints.length,
    };
  }, [dailyPoints]);

  const displayedWeatherSeriesName = useMemo(() => {
    if (!activeWeatherSeriesId) return null;
    if (activeWeatherSeriesId === activeSessionWeatherSeries?.id) return activeSessionWeatherSeries.name;
    return allMaps.find((map) => map.id === activeWeatherSeriesId)?.name || null;
  }, [activeWeatherSeriesId, activeSessionWeatherSeries, allMaps]);
  const displayedSessionWeather = Boolean(activeWeatherSeriesId && activeWeatherSeriesId === activeSession?.weatherSeriesId);

  const saving = creatingSeries || addingPoints || cloningWeather || applyingRuntimeReplay;
  const savingMap = creatingSeries || addingPoints || cloningWeather || activating || importingWeather;

  const handleYearPolicyChange = useCallback(async (mode: 'auto' | 'manual') => {
    if (!activeSession?.weatherSeriesId) return;
    try {
      await updateWeatherYearPolicy({
        seriesId: activeSession.weatherSeriesId,
        mode,
        defaultSelection: { type: 'random_year' },
      }).unwrap();
      message.success(mode === 'auto'
        ? i18n.t('simulation:weatherMap.yearPolicy.autoSet')
        : i18n.t('simulation:weatherMap.yearPolicy.manualSet'));
    } catch {
      message.error(i18n.t('simulation:weatherMap.yearPolicy.failed'));
    }
  }, [activeSession?.weatherSeriesId, updateWeatherYearPolicy]);

  return (
    <Card
      size="small"
      title={<Space><CloudOutlined /><span>{t('weatherMap.title')}</span></Space>}
      style={{ marginBottom: 16 }}
    >
      {/* Toolbar */}
      <div css={styles.toolbar}>
        <Tooltip title={t('weatherMap.toolbar.loadTooltip')}>
          <Button size="small" icon={<AppstoreOutlined />} onClick={() => setMapModalOpen(true)}>
            {t('weatherMap.toolbar.load')}
          </Button>
        </Tooltip>
        <Tooltip title={t('weatherMap.toolbar.saveTooltip')}>
          <Button size="small" icon={<SaveOutlined />} disabled={!initialized || savingMap}
            onClick={() => setSaveModalOpen(true)}>
            {t('weatherMap.toolbar.save')}
          </Button>
        </Tooltip>
        {initialized && (
          <Tooltip title={t('weatherMap.toolbar.applyTooltip')}>
            <Button size="small" type="primary" icon={<UploadOutlined />} onClick={handleApplyToSimulation} loading={saving}>
              {t('weatherMap.toolbar.apply')}
            </Button>
          </Tooltip>
        )}
        {activeSession?.weatherSeriesId && (
          <Tooltip title={t('weatherMap.yearPolicy.tooltip')}>
            <Select
              size="small"
              style={{ minWidth: 245 }}
              value={weatherYearPolicy?.mode || 'auto'}
              loading={updatingYearPolicy}
              onChange={handleYearPolicyChange}
              options={[
                { value: 'auto', label: t('weatherMap.yearPolicy.auto') },
                { value: 'manual', label: t('weatherMap.yearPolicy.manual') },
              ]}
            />
          </Tooltip>
        )}
      </div>

      {summary && (
        <div css={styles.summary}>
          {displayedWeatherSeriesName && (
            <>
              {displayedSessionWeather
                ? t('weatherMap.summary.mapApplied', { name: displayedWeatherSeriesName })
                : t('weatherMap.summary.map', { name: displayedWeatherSeriesName })}
              {' · '}
            </>
          )}
          {summary.partial
            ? t('weatherMap.summary.statsPartial', {
              temp: summary.avgTemp,
              precip: summary.totalPrecip,
              humidity: summary.avgHumidity,
              count: summary.dayCount,
            })
            : t('weatherMap.summary.statsYear', {
              temp: summary.avgTemp,
              precip: summary.totalPrecip,
              humidity: summary.avgHumidity,
            })}
        </div>
      )}

      {initialized ? (
        <WeatherCalendarEditor points={dailyPoints} onChange={setDailyPoints} />
      ) : (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#999' }}>
          <CloudOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
          <Text type="secondary">
            {activeSession?.weatherSeriesId ? t('weatherMap.loadingSessionMap') : t('weatherMap.noMap')}
          </Text>
        </div>
      )}

      {/* Map selection modal */}
      <Modal
        title={t('weatherMap.catalog.title')}
        open={mapModalOpen}
        onCancel={() => setMapModalOpen(false)}
        footer={null}
        width={760}
        loading={activating || importingWeather}
      >
        <Tabs
          items={[
            {
              key: 'maps',
              label: t('weatherMap.catalog.maps'),
              children: (
                <>
                  <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
                    <Search
                      placeholder={t('weatherMap.catalog.searchPlaceholder')}
                      value={filterText}
                      onChange={e => setFilterText(e.target.value)}
                      allowClear
                      style={{ width: 400 }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('weatherMap.catalog.userMapCount', { count: userMapCount, max: 10 })}
                    </Text>
                  </Space>
                  <Table
                    rowKey="id"
                    dataSource={filteredMaps}
                    columns={mapColumns}
                    size="small"
                    pagination={false}
                    scroll={{ y: 400 }}
                    onRow={(record) => ({ onClick: () => handleSelectMap(record), style: { cursor: 'pointer' } })}
                  />
                </>
              ),
            },
            {
              key: 'import',
              label: t('weatherMap.catalog.years'),
              children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary">{t('weatherMap.catalog.location')}</Text>
                    <Select
                      value={importLocationKey}
                      options={importLocationOptions}
                      onChange={setImportLocationKey}
                      style={{ width: '100%' }}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Space>

                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary">{t('weatherMap.catalog.year')}</Text>
                    <Select
                      value={importYear ?? importYearOptions[0]?.value}
                      options={importYearOptions}
                      onChange={setImportYear}
                      style={{ width: '100%' }}
                    />
                  </Space>

                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary">{t('weatherMap.catalog.provider')}</Text>
                    <Select
                      value={importProvider}
                      options={WEATHER_PROVIDER_OPTIONS}
                      onChange={setImportProvider}
                      style={{ width: '100%' }}
                    />
                  </Space>

                  <Checkbox
                    checked={mapImportToSessionYear}
                    disabled={!activeSession?.simDate}
                    onChange={(event) => setMapImportToSessionYear(event.target.checked)}
                  >
                    {t('weatherMap.catalog.mapToSessionYear', { year: activeSessionYear })}
                  </Checkbox>

                  <Input
                    placeholder={t('weatherMap.mapName')}
                    value={importMapName}
                    onChange={e => setImportMapName(e.target.value)}
                    onPressEnter={handleImportHistoricalWeather}
                  />

                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleImportHistoricalWeather}
                    loading={importingWeather}
                    disabled={!importYear}
                  >
                    {t('weatherMap.catalog.import')}
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Modal>

      {/* Save map modal */}
      <Modal
        title={t('weatherMap.saveModal.title')}
        open={saveModalOpen}
        onCancel={() => setSaveModalOpen(false)}
        onOk={handleSaveMap}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={savingMap}
        okButtonProps={{ disabled: !saveMapName.trim() }}
      >
        <Input
          placeholder={t('weatherMap.saveModal.namePlaceholder')}
          value={saveMapName}
          onChange={e => setSaveMapName(e.target.value)}
          onPressEnter={handleSaveMap}
          autoFocus
        />
      </Modal>
    </Card>
  );
};

export default WeatherMapPanel;
